import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Clinic } from '../../core/models/clinic.model';
import { ClinicService } from '../../core/services/clinic.service';
import { NotificationService } from '../../core/services/notification.service';
import { catchError, tap, throwError } from 'rxjs';
import { strongPasswordValidator } from '../../core/validators/password.validator';

@Component({
  selector: 'app-clinic-onboarding',
  imports: [CommonModule, ReactiveFormsModule],
  providers: [FormBuilder],
  templateUrl: './clinic-onboarding.html',
  styleUrl: './clinic-onboarding.scss',
})
export class ClinicOnboarding implements OnInit {
  form!: FormGroup;
  isLoading = false;
  errorMessage: string | null = null;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private clinicService: ClinicService,
    private notificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(150)]],
      addressLine1: ['', [Validators.required, Validators.maxLength(150)]],
      addressLine2: ['', [Validators.maxLength(150)]],
      addressLine3: ['', [Validators.maxLength(150)]],
      city: ['', [Validators.required, Validators.maxLength(80)]],
      postalCode: ['', [Validators.required, Validators.pattern(/^[0-9]{4,10}$/)]],
      // Manager Details
      managerFirstName: ['', [Validators.required, Validators.maxLength(80)]],
      managerLastName: ['', [Validators.required, Validators.maxLength(80)]],
      managerCellNumber: ['', [Validators.required, Validators.pattern(/^[0-9+\s\-]{7,15}$/)]],
      managerEmail: ['', [Validators.required, Validators.email, Validators.maxLength(150)]],
      managerPassword: [
        '',
        [Validators.required, Validators.minLength(8), Validators.maxLength(100), strongPasswordValidator()],
      ],
    });
  }

  onSubmit(): void {
    this.errorMessage = null;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;

    const formValue = this.form.value;

    const clinicPayload: Clinic = {
      name: formValue.name,
      addressLine1: formValue.addressLine1,
      addressLine2: formValue.addressLine2 || undefined,
      addressLine3: formValue.addressLine3 || undefined,
      city: formValue.city,
      postalCode: formValue.postalCode,
      manager: {
        firstName: formValue.managerFirstName,
        lastName: formValue.managerLastName,
        phoneNumber: formValue.managerCellNumber,
        email: formValue.managerEmail,
        password: formValue.managerPassword,
      },
    };

    this.clinicService
      .createClinic(clinicPayload)
      .pipe(
        tap(() => {
          this.isLoading = false;
          this.notificationService.success(
            'Practice registered successfully!',
            'Registration Complete',
          );
          this.router.navigate(['dashboard/doctor-dashboard']);
        }),
        catchError((error) => {
          this.isLoading = false;
          const msg =
            error?.error?.message ??
            'Failed to register clinic. Please verify details and try again.';
          this.errorMessage = msg;
          this.notificationService.error(msg, 'Registration Error');
          return throwError(() => error);
        }),
      )
      .subscribe();
  }
}
